import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  LEAD_TIME_OVERRIDE_DAYS_MAX,
  LEAD_TIME_OVERRIDE_DAYS_MIN,
  LEAD_TIME_OVERRIDE_SCOPES,
} from "@erp/shared-types";
import {
  OverrideWindowShape,
  withOverrideWindowRules,
} from "./override-window";

// Manual lead-time overrides (im_lead_time_overrides): a buyer pins the lead
// time for one SKU x warehouse over a window; forecast policy math and the
// replenishment engine use it in place of the observed estimate while it is
// active. Rows are immutable history: "update" revokes the current row and
// creates its replacement. Products are ProductV3 only (the key is the SKU).
// Plan: docs/procurement/lead-time-overrides.md.

// ─── Shared schemas ─────────────────────────────────────────────────────────

/** Derived from the shared registry; v1 exposes `item_loc` only. */
export const LeadTimeOverrideScopeSchema = z.enum(LEAD_TIME_OVERRIDE_SCOPES);

export const LeadTimeOverrideDaysSchema = z
  .number()
  .int()
  .min(LEAD_TIME_OVERRIDE_DAYS_MIN)
  .max(LEAD_TIME_OVERRIDE_DAYS_MAX);

/** One override row, active or historical. */
export const LeadTimeOverrideRowSchema = z.object({
  id: z.string().uuid(),
  scope: LeadTimeOverrideScopeSchema,
  /** ProductV3 key (the SKU). Null only for a future scope that has no product. */
  productV3Key: z.string().nullable(),
  warehouseId: z.string().uuid().nullable(),
  supplierId: z.string().uuid().nullable(),
  leadTimeDays: z.number().int(),
  startsAt: z.string(),
  endsAt: z.string().nullable(),
  reviewAt: z.string().nullable(),
  reason: z.string().nullable(),
  createdByUserId: z.string().uuid(),
  createdByUserName: z.string().nullable(),
  createdAt: z.string(),
  deactivatedAt: z.string().nullable(),
  deactivatedByUserId: z.string().uuid().nullable(),
  deactivatedByUserName: z.string().nullable(),
  /** The row this one replaced (set by "update"); null for a fresh override. */
  supersedesOverrideId: z.string().uuid().nullable(),
  /** True iff applying right now: not revoked and within [startsAt, endsAt). */
  isActive: z.boolean(),
  /** True iff active and `reviewAt` has passed. */
  needsReview: z.boolean(),
});
export type LeadTimeOverrideRow = z.infer<typeof LeadTimeOverrideRowSchema>;

/** The series an item_loc override targets: one SKU (ProductV3 key) at one warehouse. */
const SeriesTargetShape = {
  warehouseId: z.string().uuid(),
  productV3Key: z.string().min(1),
} as const;

// ─── Create ─────────────────────────────────────────────────────────────────

export const CreateLeadTimeOverrideInputSchema = withOverrideWindowRules(
  z.object({
    ...SeriesTargetShape,
    /** v1 accepts `item_loc` only; defaults to it so callers may omit it. */
    scope: LeadTimeOverrideScopeSchema.default("item_loc"),
    leadTimeDays: LeadTimeOverrideDaysSchema,
    ...OverrideWindowShape,
    reason: z.string().max(1000).nullable().optional(),
  }),
);

export const CreateLeadTimeOverrideResponseSchema = z.object({
  override: LeadTimeOverrideRowSchema,
  /** Whether the series' policy levels were recomputed with the new value. */
  recomputed: z.boolean(),
});

export type CreateLeadTimeOverrideInput = z.infer<
  typeof CreateLeadTimeOverrideInputSchema
>;
export type CreateLeadTimeOverrideResponse = z.infer<
  typeof CreateLeadTimeOverrideResponseSchema
>;

// ─── List (one series, active + history) ────────────────────────────────────

export const ListLeadTimeOverridesInputSchema = z.object(SeriesTargetShape);

export const ListLeadTimeOverridesResponseSchema = z.object({
  /** Newest first, revoked and lapsed rows included. */
  overrides: z.array(LeadTimeOverrideRowSchema),
  /** The row the resolver applies right now (last-writer-wins), or null. */
  active: LeadTimeOverrideRowSchema.nullable(),
});

export type ListLeadTimeOverridesInput = z.infer<
  typeof ListLeadTimeOverridesInputSchema
>;
export type ListLeadTimeOverridesResponse = z.infer<
  typeof ListLeadTimeOverridesResponseSchema
>;

// ─── Update (revoke + create replacement) ───────────────────────────────────

export const UpdateLeadTimeOverrideInputSchema = withOverrideWindowRules(
  z.object({
    overrideId: z.string().uuid(),
    leadTimeDays: LeadTimeOverrideDaysSchema,
    ...OverrideWindowShape,
    reason: z.string().max(1000).nullable().optional(),
  }),
);

export const UpdateLeadTimeOverrideResponseSchema = z.object({
  /** The replacement row (now active). */
  override: LeadTimeOverrideRowSchema,
  /** The row it replaced, stamped as revoked by the editor. */
  superseded: LeadTimeOverrideRowSchema,
  recomputed: z.boolean(),
});

export type UpdateLeadTimeOverrideInput = z.infer<
  typeof UpdateLeadTimeOverrideInputSchema
>;
export type UpdateLeadTimeOverrideResponse = z.infer<
  typeof UpdateLeadTimeOverrideResponseSchema
>;

// ─── Revoke ──────────────────────────────────────────────────────────────────

export const RevokeLeadTimeOverrideInputSchema = z.object({
  overrideId: z.string().uuid(),
});

export const RevokeLeadTimeOverrideResponseSchema = z.object({
  override: LeadTimeOverrideRowSchema,
  recomputed: z.boolean(),
});

export type RevokeLeadTimeOverrideInput = z.infer<
  typeof RevokeLeadTimeOverrideInputSchema
>;
export type RevokeLeadTimeOverrideResponse = z.infer<
  typeof RevokeLeadTimeOverrideResponseSchema
>;

// ─── Contract ───────────────────────────────────────────────────────────────

export const leadTimeOverridesContracts = {
  create: oc
    .input(CreateLeadTimeOverrideInputSchema)
    .output(CreateLeadTimeOverrideResponseSchema)
    .route({
      method: "POST",
      summary: "Override a SKU's lead time",
      description:
        "Pins the lead time in days for a SKU at a warehouse over a window (expiry or review date required) and recomputes its safety stock and reorder levels.",
    }),
  list: oc
    .input(ListLeadTimeOverridesInputSchema)
    .output(ListLeadTimeOverridesResponseSchema)
    .route({
      method: "GET",
      summary: "List lead time overrides for a SKU",
      description:
        "Returns active and historical lead-time overrides for a SKU at one warehouse and identifies the one that applies right now.",
    }),
  update: oc
    .input(UpdateLeadTimeOverrideInputSchema)
    .output(UpdateLeadTimeOverrideResponseSchema)
    .route({
      method: "POST",
      summary: "Update a lead time override",
      description:
        "Edits an active lead-time override by revoking it and creating a linked replacement in one transaction; policy levels are recomputed.",
    }),
  revoke: oc
    .input(RevokeLeadTimeOverrideInputSchema)
    .output(RevokeLeadTimeOverrideResponseSchema)
    .route({
      method: "POST",
      summary: "Revoke a lead time override",
      description:
        "Ends a lead-time override immediately so the observed lead-time estimate applies again, and recomputes the SKU's policy levels.",
    }),
} as const;
